import { useQuery } from '@tanstack/react-query';
import { ApiError as ApiErrorSchema, BadgeTemplate, Field, Site, page } from '@cloudsign/shared';
import type { BadgeLayout, BadgePreviewRequest } from '@cloudsign/shared';
import { z } from 'zod';
import { ApiClientError, api } from '../../lib/api';

export const TemplateList = z.array(BadgeTemplate);
const SitePage = page(Site);

export type StatusFilter = 'active' | 'archived' | 'all';

export const templatesKey = (status: StatusFilter, siteId?: string) => ['badges', 'templates', status, siteId ?? null] as const;
export const templateKey = (id: string) => ['badges', 'template', id] as const;

export function useTemplates(status: StatusFilter, siteId?: string) {
  return useQuery({
    queryKey: templatesKey(status, siteId),
    queryFn: () => api.get('/badges/templates', { query: { status, siteId }, schema: TemplateList }),
  });
}

export function useTemplate(id: string | undefined) {
  return useQuery({
    queryKey: templateKey(id ?? ''),
    enabled: Boolean(id),
    queryFn: () => api.get(`/badges/templates/${id}`, { schema: BadgeTemplate }),
  });
}

/** Every site (any status) so a design can name its site. */
export function useAllSites() {
  return useQuery({
    queryKey: ['badges', 'sites'],
    queryFn: () => api.get('/sites', { query: { status: 'all', pageSize: 200 }, schema: SitePage }),
  });
}

/** The site's sign-in fields, for the "data" choices of a text element. */
export function useSignInFields(siteId: string | null | undefined) {
  return useQuery({
    queryKey: ['badges', 'fields', siteId],
    enabled: Boolean(siteId),
    queryFn: () => api.get(`/fields/sites/${siteId}`, { query: { scope: 'sign_in' }, schema: z.array(Field) }),
  });
}

/** POST /badges/preview answers with a PDF, which the JSON client cannot read, so this fetches it directly. */
export async function fetchPreviewPdf(body: Pick<BadgePreviewRequest, 'sample'> & { layout: BadgeLayout; templateId: string | null }): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch('/api/v1/badges/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/pdf' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiClientError(0, { code: 'internal', messageKey: 'error.network' });
  }
  if (!res.ok) {
    let json: unknown;
    try {
      json = await res.json();
    } catch {
      json = undefined;
    }
    const parsed = ApiErrorSchema.safeParse(json);
    throw new ApiClientError(res.status, parsed.success ? parsed.data.error : { code: 'internal', messageKey: 'error.internal' });
  }
  return res.blob();
}
